import {
  type CreateLockerverseAuctionOptions,
  createLockerverseAuctionClient,
  type LockerverseAuction,
  type LockerverseAuctionClient,
  type LockerverseSdkError,
} from "@lockerverse/sdk/auctions";
import { useCallback, useEffect, useRef, useState } from "react";
import { useImmutableResourceIdentity } from "./immutable-resource-identity.ts";
import {
  type AuctionRefreshOptions,
  useAuctionReturnRefresh,
} from "./use-auction-return-refresh.ts";

export type UseLockerverseAuctionsOptions = AuctionRefreshOptions & {
  /** Initial server snapshot. The hook still refreshes on mount. */
  initialAuctions?: LockerverseAuction[];
};

export type UseLockerverseAuctionsResult = {
  client: LockerverseAuctionClient;
  auctions: LockerverseAuction[] | null;
  loading: boolean;
  error: LockerverseSdkError | null;
  refresh: () => void;
};

type AuctionLoadState = Pick<
  UseLockerverseAuctionsResult,
  "auctions" | "error" | "loading"
>;

export function useLockerverseAuctions(
  options: CreateLockerverseAuctionOptions,
  refreshOptions: UseLockerverseAuctionsOptions = {}
): UseLockerverseAuctionsResult {
  useImmutableResourceIdentity("auctions", {
    apiBaseUrl: options.apiBaseUrl,
    communitySlug: options.communitySlug,
    environment: options.environment,
    mediaBaseUrl: options.mediaBaseUrl,
    slug: "",
  });
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;
  const [client] = useState(() =>
    createLockerverseAuctionClient({
      ...options,
      onError: (error) => onErrorRef.current?.(error),
    })
  );
  const generation = useRef(0);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<AuctionLoadState>(() => ({
    auctions: refreshOptions.initialAuctions ?? null,
    error: null,
    loading: true,
  }));
  const refresh = useCallback(() => {
    generation.current += 1;
    setState((current) => ({ ...current, error: null, loading: true }));
    setRevision((value) => value + 1);
  }, []);
  const startRead = useAuctionReturnRefresh(refresh, refreshOptions);

  useEffect(() => {
    let active = true;
    const requestGeneration = generation.current;
    const settleRead = startRead();
    client
      .list()
      .then(
        (auctions) => {
          if (active && requestGeneration === generation.current) {
            setState({ auctions, error: null, loading: false });
          }
        },
        (error: LockerverseSdkError) => {
          if (active && requestGeneration === generation.current) {
            setState((current) => ({ ...current, error, loading: false }));
          }
        }
      )
      .finally(settleRead);
    return () => {
      active = false;
    };
  }, [client, revision, startRead]);

  return {
    ...state,
    client,
    refresh,
  };
}
